/**
 * CANONICAL CHARGE-LEVEL BENCHMARK STATUS.
 *
 * One resolver that decides, for every charge in the invoice inventory,
 * whether that charge carries market evidence and where it sits against the
 * market. Chapter 01 (Case Brief & Invoice Overview), Chapter 02 (Market
 * Benchmark Analysis) and the immutable Report Summary all consume THIS
 * module. No chapter may classify a charge on its own.
 *
 * Before this module existed the two chapters resolved coverage differently:
 *   • Chapter 01 bound `market_comparisons` to inventory charges, then fell
 *     back to stored `market_research` per service family and finally to
 *     benchmark reuse across compatible sibling lines.
 *   • The Report Summary (and therefore Chapter 02's counts) matched raw
 *     benchmark rows by normalized service name only, with no family binding
 *     and no reuse.
 * That produced 17 supported / 1 pending in Chapter 01 and 11 supported /
 * 7 pending in Chapter 02 for the same 18 charges. This resolver removes the
 * possibility of that divergence.
 *
 * Pure and deterministic: no network, no AI, no invented pricing.
 */

import type { MarketComparison, MarketResearch } from "@/lib/db/models";
import {
  buildChargeInventory,
  normalizeChargeKey,
  parseChargeDescription,
  type InventoryCharge,
} from "./charge-inventory";
import {
  resolveReusedEvidence,
  type ReuseCandidate,
  type ReuseRejectionReason,
} from "./benchmark-reuse";

/** The one vocabulary both chapters may use for a charge-level outcome. */
export type CanonicalBenchmarkStatus =
  | "materially-above"
  | "above"
  | "within"
  | "below"
  | "pending"
  /**
   * Printed line that repeats an earlier printed line. It is never deleted and
   * never benchmarked — the vendor must confirm whether it is a real second
   * charge before any recovery can be quantified against it.
   */
  | "clarification";

export const CANONICAL_STATUS_LABEL: Record<CanonicalBenchmarkStatus, string> = {
  "materially-above": "Materially Above Market",
  above: "Above Market",
  within: "Within Market",
  below: "Below Market",
  pending: "Requires Additional Market Support",
  clarification: "Possible Duplicate Billing — Vendor Clarification Required",
};

/** Where the market evidence for a charge came from. */
export type ChargeEvidenceBasis =
  | "verified_benchmark"
  | "researched"
  | "reused"
  | "pending"
  | "clarification";

export interface CanonicalChargeStatus {
  /** Inventory charge identity — the join key across every chapter. */
  chargeId: string;
  charge: InventoryCharge;
  /** Position of this charge in the canonical presentation order. */
  order: number;
  quantity: number;
  invoiceAmount: number | null;
  invoiceRate: number | null;
  marketLow: number | null;
  marketHigh: number | null;
  marketAverage: number | null;
  /** Market average × this line's own quantity. Never borrowed. */
  expectedAmount: number | null;
  /** Vendor amount − supported amount. Null when pending. */
  difference: number | null;
  variancePct: number | null;
  status: CanonicalBenchmarkStatus;
  /** True for every status other than `pending`. */
  supported: boolean;
  evidenceBasis: ChargeEvidenceBasis;
  /** Bound `market_comparisons` row, when one attached to this charge. */
  comparison: MarketComparison | null;
  /** Stored `market_research` row for this charge's service family. */
  research: MarketResearch | null;
  /** Charge id whose verified rate this line borrowed, when reused. */
  reusedFromChargeId: string | null;
  /** Why reuse was declined, when the charge remains pending. */
  reuseRejectionReason: ReuseRejectionReason | null;
}

export interface ChargeBenchmarkResolution {
  /** Inventory charges in canonical presentation order. */
  charges: InventoryCharge[];
  statuses: CanonicalChargeStatus[];
  byChargeId: Map<string, CanonicalChargeStatus>;
  counts: {
    reviewed: number;
    supported: number;
    pending: number;
    /** Subset of `pending` held for vendor duplicate clarification. */
    clarification: number;
    /** Subset of `pending` awaiting comparable market pricing. */
    pendingEvidence: number;
    materiallyAbove: number;
    above: number;
    within: number;
    below: number;
  };
  /** Vendor dollars covered by market evidence. */
  supportedVendorTotal: number;
  /** Supported (market) dollars for the same scope. */
  supportedExpectedTotal: number;
  /**
   * CERTIFIED SUPPORTED RECOVERY — vendor dollars less supported dollars across
   * the market-supported scope. This is the identical identity Chapter 02
   * prints as "Total Supported Recovery Within Supported Scope", so no surface
   * may derive recovery any other way.
   */
  supportedRecoveryTotal: number;
}

/* ─────────────────── benchmark → inventory binding ─────────────────── */

export function normalizeChargeDescription(v: string | null | undefined): string {
  return (v ?? "")
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Generic qualifiers a benchmark label may add to an invoice charge name. */
export const BENCHMARK_QUALIFIER_TOKENS = new Set([
  "support",
  "unit",
  "service",
  "per",
  "period",
  "rate",
  "rates",
  "daily",
  "hourly",
  "fee",
  "charge",
  "standard",
  "each",
  "day",
  "hour",
]);

/**
 * Binds a benchmark row back to the INVENTORY CHARGE it describes.
 *
 * Matching happens against the deduplicated invoice inventory — never against
 * raw OCR lines — so a benchmark can only ever attach to a charge that is
 * actually rendered. A benchmark that binds to nothing produces no row.
 */
export function findInventoryChargeForMarketRow(
  market: MarketComparison,
  charges: InventoryCharge[],
  /* Charges already bound to an earlier market row. Without this, two market
     rows matching the same charge would each claim its amount and
     double-count it in the totals. */
  used: Set<InventoryCharge>,
): InventoryCharge | null {
  const available = charges.filter((c) => !used.has(c));
  if (!available.length) return null;
  const marketDescription = market.line_item ?? market.service_type ?? "";
  const normalizedMarket = normalizeChargeDescription(marketDescription);
  const marketIsStorage = /storage/i.test(marketDescription);

  if (marketIsStorage) {
    const storageCharge = available.find((c) => /storage/i.test(c.familyName));
    if (storageCharge) return storageCharge;
  }

  const substringMatch = available.find((c) => {
    const normalizedLine = normalizeChargeDescription(c.description);
    if (!normalizedLine || !normalizedMarket) return false;
    return (
      normalizedLine === normalizedMarket ||
      normalizedLine.includes(normalizedMarket) ||
      normalizedMarket.includes(normalizedLine)
    );
  });
  if (substringMatch) return substringMatch;

  /* Substring matching alone fails whenever OCR carries supporting detail in
     the same cell ("Recovery Van\n<unit>"). Fall back to the parsed SERVICE
     FAMILY key so the benchmark still attaches to the real invoice charge
     instead of being rendered as a charge of its own. */
  const marketFamilyKey = normalizeChargeKey(
    parseChargeDescription(marketDescription).familyName,
  );
  if (!marketFamilyKey) return null;
  const exact = available.find((c) => c.normalized === marketFamilyKey);
  if (exact) return exact;

  /* Benchmark labels often add a qualifier the invoice does not carry
     ("Recovery Van / Support Unit" vs "Recovery Van"). Bind when the charge
     family is a leading token-run of the benchmark label and everything left
     over is a generic qualifier — never on an arbitrary partial word. */
  const marketTokens = marketFamilyKey.split(" ").filter(Boolean);
  return (
    available.find((c) => {
      const tokens = c.normalized.split(" ").filter(Boolean);
      if (!tokens.length || tokens.length >= marketTokens.length) return false;
      if (!tokens.every((t, i) => marketTokens[i] === t)) return false;
      return marketTokens
        .slice(tokens.length)
        .every((t) => BENCHMARK_QUALIFIER_TOKENS.has(t));
    }) ?? null
  );
}

/* ─────────────────────────── classification ─────────────────────────── */

/**
 * Single variance classifier. Identical thresholds to
 * `market-evidence.classifyVariance`, so a charge can never read "Above
 * Market" in one chapter and "Within Market" in another.
 */
export function classifyCanonicalVariance(pct: number | null): CanonicalBenchmarkStatus {
  if (pct === null || !Number.isFinite(pct)) return "pending";
  if (pct >= 25) return "materially-above";
  if (pct >= 8) return "above";
  if (pct <= -8) return "below";
  return "within";
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/* ─────────────────────────────── resolver ─────────────────────────────── */

export interface ChargeBenchmarkInputs {
  lineItems?: Array<Record<string, unknown>> | null;
  marketComparisons?: MarketComparison[] | null;
  marketResearch?: MarketResearch[] | null;
}

export function resolveChargeBenchmarkStatuses(
  inputs: ChargeBenchmarkInputs,
): ChargeBenchmarkResolution {
  const { canonical: charges } = buildChargeInventory(inputs.lineItems ?? []);
  /* Duplicate printed lines stay in the inventory (the printed invoice is
     authoritative) but never take a benchmark, never lend rate evidence and
     never enter the supported-recovery scope. */
  const benchmarkable = charges.filter((c) => !c.duplicateSuspect);
  const markets = (inputs.marketComparisons ?? []).filter(Boolean);
  const research = (inputs.marketResearch ?? []).filter(Boolean);

  const researchByKey = new Map<string, MarketResearch>();
  for (const r of research) {
    const key = normalizeChargeKey(r.charge_key || r.charge_description);
    if (key && !researchByKey.has(key)) researchByKey.set(key, r);
    if (r.charge_key && !researchByKey.has(r.charge_key)) researchByKey.set(r.charge_key, r);
  }
  const researchFor = (charge: InventoryCharge): MarketResearch | null =>
    researchByKey.get(charge.normalized) ?? null;

  /* Bind each benchmark row to exactly one inventory charge. */
  const boundMarket = new Map<InventoryCharge, MarketComparison>();
  const consumed = new Set<InventoryCharge>();
  const orderedBound: InventoryCharge[] = [];
  for (const m of markets) {
    const charge = findInventoryChargeForMarketRow(m, benchmarkable, consumed);
    if (!charge) continue;
    consumed.add(charge);
    boundMarket.set(charge, m);
    orderedBound.push(charge);
  }

  /* Benchmark-backed charges keep their position, remaining charges follow. */
  const ordered = [...orderedBound, ...charges.filter((c) => !consumed.has(c))];

  /* Charges carrying proven rate evidence; compatible siblings may borrow the
     RATE from these — never the amount. */
  const reuseCandidates: ReuseCandidate[] = [];
  for (const charge of benchmarkable) {
    const m = boundMarket.get(charge);
    const avg = m ? (m.local_average ?? m.market_median ?? null) : null;
    if (m && avg !== null) {
      reuseCandidates.push({
        charge,
        evidence: {
          sourceChargeId: charge.id,
          marketLow: m.typical_low ?? m.market_low ?? null,
          marketHigh: m.typical_high ?? m.market_high ?? null,
          marketAverage: avg,
          sourceLabel: "Verified market benchmark",
          researchNote: null,
        },
      });
      continue;
    }
    const r = researchFor(charge);
    if (r && typeof r.market_average === "number") {
      reuseCandidates.push({
        charge,
        evidence: {
          sourceChargeId: charge.id,
          marketLow: r.market_low ?? null,
          marketHigh: r.market_high ?? null,
          marketAverage: r.market_average,
          sourceLabel: "Researched market pricing",
          researchNote: r.methodology_note ?? null,
        },
      });
    }
  }

  const statuses: CanonicalChargeStatus[] = ordered.map((charge, order) => {
    const comparison = boundMarket.get(charge) ?? null;
    const invoiceAmount = charge.amount;
    const invoiceRate = charge.rate;
    const qtyRaw =
      charge.quantity ??
      (invoiceAmount !== null && invoiceRate && invoiceRate > 0
        ? round2(invoiceAmount / invoiceRate)
        : null);
    const quantity = qtyRaw !== null && qtyRaw > 0 ? qtyRaw : 1;

    let marketLow: number | null = null;
    let marketHigh: number | null = null;
    let marketAverage: number | null = null;
    let evidenceBasis: ChargeEvidenceBasis = "pending";
    let reusedFromChargeId: string | null = null;
    let reuseRejectionReason: ReuseRejectionReason | null = null;
    const researchRow = charge.duplicateSuspect ? null : researchFor(charge);

    if (comparison && !charge.duplicateSuspect) {
      marketLow = comparison.typical_low ?? comparison.market_low ?? null;
      marketHigh = comparison.typical_high ?? comparison.market_high ?? null;
      marketAverage = comparison.local_average ?? comparison.market_median ?? null;
      if (marketAverage !== null) evidenceBasis = "verified_benchmark";
    }

    if (marketAverage === null && researchRow && typeof researchRow.market_average === "number") {
      marketLow = researchRow.market_low ?? null;
      marketHigh = researchRow.market_high ?? null;
      marketAverage = researchRow.market_average;
      evidenceBasis = "researched";
    }

    if (marketAverage === null && !charge.duplicateSuspect) {
      const decision = resolveReusedEvidence(charge, reuseCandidates);
      if (decision.evidence) {
        marketLow = decision.evidence.marketLow;
        marketHigh = decision.evidence.marketHigh;
        marketAverage = decision.evidence.marketAverage;
        evidenceBasis = "reused";
        reusedFromChargeId = decision.evidence.sourceChargeId;
      } else {
        reuseRejectionReason = decision.reason;
      }
    }

    const expectedAmount =
      marketAverage !== null && marketAverage > 0 ? round2(marketAverage * quantity) : null;
    const difference =
      invoiceAmount !== null && expectedAmount !== null
        ? round2(invoiceAmount - expectedAmount)
        : null;
    const variancePct =
      invoiceAmount !== null && expectedAmount !== null && expectedAmount > 0
        ? ((invoiceAmount - expectedAmount) / expectedAmount) * 100
        : null;
    const status: CanonicalBenchmarkStatus = charge.duplicateSuspect
      ? "clarification"
      : expectedAmount === null
        ? "pending"
        : classifyCanonicalVariance(variancePct);

    return {
      chargeId: charge.id,
      charge,
      order,
      quantity,
      invoiceAmount,
      invoiceRate,
      marketLow: expectedAmount !== null ? marketLow : null,
      marketHigh: expectedAmount !== null ? marketHigh : null,
      marketAverage: expectedAmount !== null ? marketAverage : null,
      expectedAmount,
      difference,
      variancePct,
      status,
      supported: status !== "pending" && status !== "clarification",
      evidenceBasis:
        status === "clarification"
          ? "clarification"
          : expectedAmount !== null
            ? evidenceBasis
            : "pending",
      comparison,
      research: researchRow,
      reusedFromChargeId,
      reuseRejectionReason,
    };
  });

  const byChargeId = new Map(statuses.map((s) => [s.chargeId, s]));
  const counts = {
    reviewed: statuses.length,
    supported: statuses.filter((s) => s.supported).length,
    /* Every unsupported charge, whatever the reason. Keeps the certified
       invariant Reviewed = Benchmarked + Pending intact. */
    pending: statuses.filter((s) => !s.supported).length,
    /* Subset of `pending`: held out for vendor clarification, not for lack of
       market evidence. */
    clarification: statuses.filter((s) => s.status === "clarification").length,
    /* Subset of `pending`: genuinely awaiting comparable pricing. */
    pendingEvidence: statuses.filter((s) => s.status === "pending").length,
    materiallyAbove: statuses.filter((s) => s.status === "materially-above").length,
    above: statuses.filter((s) => s.status === "above").length,
    within: statuses.filter((s) => s.status === "within").length,
    below: statuses.filter((s) => s.status === "below").length,
  };
  const supportedStatuses = statuses.filter((s) => s.supported && s.expectedAmount !== null);
  const supportedVendorTotal = round2(
    supportedStatuses.reduce((sum, s) => sum + (s.invoiceAmount ?? 0), 0),
  );
  const supportedExpectedTotal = round2(
    supportedStatuses.reduce((sum, s) => sum + (s.expectedAmount ?? 0), 0),
  );
  const supportedRecoveryTotal = round2(supportedVendorTotal - supportedExpectedTotal);

  return {
    charges: ordered,
    statuses,
    byChargeId,
    counts,
    supportedVendorTotal,
    supportedExpectedTotal,
    supportedRecoveryTotal,
  };
}
